import { differenceInDays } from "date-fns";

import { Filter, TagFilter } from "constants/filters";
import { ReportDistribution } from "constants/reports/distribution";
import { ReportType } from "constants/reports/report-type";
import { ensureArray, ensureFlat } from "helpers/data";
import { deserializeCustomDateRangeURLParams, mapDateRangeToQueryString, mapDateToQueryString } from "helpers/filters";
import { getFiltersByReportType } from "helpers/reports";
import { IKeyValue, stringifyQueryParams } from "helpers/url";
import { IStoreState } from "interfaces/storeState";

import { IReportsViewParams } from "./interfaces";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { getCurrentView, getDistribution, getFilters } from "./selectors";

// FIXME: Fix this the next time the file is edited.
// @ts-expect-error TS(6133): 'pathname' is declared but its value is never read... Remove this comment to see the full error message
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function parseReportsViewURLParams(queryParams: IKeyValue<string>, pathname?: string): IReportsViewParams {
  const queryObject: IReportsViewParams = {};

  const dateRangeFilter = deserializeCustomDateRangeURLParams(
    queryParams.date_from as string,
    queryParams.date_to as string,
    queryParams.compare_date_from as string,
    queryParams.compare_date_to as string
  );

  if (dateRangeFilter) {
    // @ts-expect-error TS(2345)
    let diff = Math.abs(differenceInDays(dateRangeFilter.from, dateRangeFilter.to));
    if (dateRangeFilter.compare) {
      diff = Math.max(
        // @ts-expect-error TS(2345)
        Math.abs(differenceInDays(dateRangeFilter.from, dateRangeFilter.to)),
        // @ts-expect-error TS(2345)
        Math.abs(differenceInDays(dateRangeFilter.compare.from, dateRangeFilter.compare.to))
      );
    }

    queryObject.distribution = diff > 183 ? ReportDistribution.Month : ReportDistribution.Day;
  }

  if (dateRangeFilter) {
    queryObject[Filter.DateRange] = dateRangeFilter;
  }

  if (queryParams.tag) {
    queryObject[Filter.Tag] = ensureArray(queryParams.tag);
  }

  if (queryParams.tagged && queryParams.tagged === "0") {
    queryObject[Filter.Tag] = [TagFilter.NotTagged, ...(queryObject[Filter.Tag] || [])];
  }

  if (queryParams.agent) {
    queryObject[Filter.Agent] = {
      agents: ensureArray(queryParams.agent),
      compare: ensureArray(queryParams.compare_agent),
    };
  }

  if (queryParams.group) {
    queryObject[Filter.Group] = {
      groups: ensureArray(queryParams.group),
      compare: ensureArray(queryParams.compare_group),
    };
  }

  if (queryParams.detailedSource) {
    queryObject[Filter.DetailedSource] = ensureArray(queryParams.detailedSource);
  }

  if (queryParams.author) {
    queryObject[Filter.Author] = ensureArray(queryParams.author).join(",");
  }
  if (queryParams.action) {
    queryObject[Filter.Action] = ensureArray(queryParams.action).join(",");
  }

  if (queryParams.target) {
    queryObject[Filter.Target] = ensureArray(queryParams.target).join(",");
  }
  if (queryParams.page_size) {
    queryObject[Filter.PageSize] = Number(ensureArray(queryParams.page_size).join(",")) + 1;
  }
  if (queryParams[Filter.NextId]) {
    queryObject[Filter.NextId] = ensureArray(queryParams[Filter.NextId]).join(",");
  }
  if (queryParams[Filter.PrevId]) {
    queryObject[Filter.PrevId] = ensureArray(queryParams[Filter.PrevId]).join(",");
  }
  if (queryParams[Filter.NextValue]) {
    queryObject[Filter.NextValue] = ensureArray(queryParams[Filter.NextValue]).join(",");
  }
  if (queryParams[Filter.PrevValue]) {
    queryObject[Filter.PrevValue] = ensureArray(queryParams[Filter.PrevValue]).join(",");
  }

  if (queryParams[Filter.Language]) {
    queryObject[Filter.Language] = ensureArray(queryParams[Filter.Language]);
  }

  if (queryParams.priority) {
    queryObject[Filter.Priority] = {
      priority: ensureArray(queryParams.priority).map(p => parseInt(p, 10)),
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...(queryParams.priority_op ? { priorityOp: queryParams.priority_op as any } : {}),
    };
  }

  if (queryParams.group_by) {
    queryObject.distribution = queryParams.group_by as ReportDistribution;
  }

  return queryObject;
}

export function getReportsViewQueryParams(state: IStoreState): IKeyValue {
  const page = getCurrentView(state);

  if ([ReportType.Last7days, "dashboard"].includes(page)) {
    return {};
  }

  // @ts-expect-error TS(2339): Property 'agent' does not exist on type 'IReportsV... Remove this comment to see the full error message
  const { agent, dateRange, group, tag, date, priority, detailedSource, action, target, author, pageSize, language } =
    getFilters(state);
  const nextId = getFilters(state)[Filter.NextId];
  const prevId = getFilters(state)[Filter.PrevId];
  const nextValue = getFilters(state)[Filter.NextValue];
  const prevValue = getFilters(state)[Filter.PrevValue];
  const distribution = getDistribution(state);
  const availableFilters = getFiltersByReportType(page);
  // @ts-expect-error TS(2345)
  const dateRangeParams = mapDateRangeToQueryString(dateRange);
  const dateParam = mapDateToQueryString(date);
  let queryParams: IKeyValue<string | number> = {};

  if (agent && agent.agents && agent.agents.length && availableFilters.includes(Filter.Agent)) {
    queryParams.agent = ensureArray(agent.agents);

    if (agent.compare && agent.compare.length) {
      queryParams.compare_agent = ensureFlat(agent.compare);
    }
  }

  if (dateRangeParams && availableFilters.includes(Filter.DateRange)) {
    queryParams = {
      ...queryParams,
      ...dateRangeParams,
    };
  }

  // @ts-expect-error TS(2399)
  if (dateParam && dateParam.date && availableFilters.includes(Filter.Date)) {
    queryParams.date = dateParam.date;
  }

  if (group && group.groups && group.groups.length && availableFilters.includes(Filter.Group)) {
    queryParams.group = ensureArray(group.groups);

    if (group.compare && group.compare.length) {
      queryParams.compare_group = ensureFlat(group.compare);
    }
  }

  if (tag && availableFilters.includes(Filter.Tag)) {
    if (tag.includes(TagFilter.NotTagged)) {
      queryParams.tagged = "0";
    } else {
      queryParams.tag = ensureArray(tag);
    }
  }

  if (detailedSource && availableFilters.includes(Filter.DetailedSource)) {
    queryParams.detailedSource = ensureArray(detailedSource);
  }

  if (author && availableFilters.includes(Filter.Author)) {
    queryParams.author = author;
  }
  if (action && availableFilters.includes(Filter.Action)) {
    queryParams.action = action;
  }
  if (target && availableFilters.includes(Filter.Target)) {
    queryParams.target = target;
  }
  if (pageSize && availableFilters.includes(Filter.PageSize)) {
    queryParams.page_size = pageSize - 1;
  }
  if (prevId && availableFilters.includes(Filter.PrevId)) {
    queryParams[Filter.PrevId] = prevId;
  }
  if (nextId && availableFilters.includes(Filter.NextId)) {
    queryParams[Filter.NextId] = nextId;
  }
  if (nextValue && availableFilters.includes(Filter.NextValue)) {
    queryParams[Filter.NextValue] = nextValue;
  }
  if (prevValue && availableFilters.includes(Filter.PrevValue)) {
    queryParams[Filter.PrevValue] = prevValue;
  }

  if (distribution !== ReportDistribution.Day && distribution !== ReportDistribution.Month) {
    queryParams.group_by = distribution;
  }

  if (priority) {
    queryParams.priority = ensureArray(priority.priority).map(p => p.toString());
    if (priority.priorityOp) {
      queryParams.priority_op = priority.priorityOp;
    }
  }

  if (language && availableFilters.includes(Filter.Language)) {
    queryParams.language = ensureArray(language);
  }

  return queryParams;
}

export function buildReportsViewQueryString(state: IStoreState): string {
  return stringifyQueryParams(getReportsViewQueryParams(state));
}
